import type {
    FC
} from "react";
import {
    View
} from "react-native";
import type ISwipeableRowHoverProps from "./type";
import stylesheet, {
    getSwipeableRowHoverTooltip,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    Ellipsis as EllipsisIcon
} from "lucide-react-native";
import AnimatedIcon from "../../../animatedIcon";
import DropdownMenu from "../../../dropdownMenu";
import StateLayer from "../../../stateLayer";
import Tooltip from "../../../tooltip";
import Button from "../../../button";

const SWIPEABLE_ROW_MAX_BUTTONS = 3;

const SwipeableRowMoreIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    animation="signature"
    icon={EllipsisIcon}
    color={color}
    size={size}
/>;

const handleNoop = () => {};

const SwipeableRowHover: FC<ISwipeableRowHoverProps> = ({
    onMenuOpenChange,
    customLocalize,
    onActionPress,
    borderRadius,
    customTheme,
    isHovered,
    isVisible,
    menuItems,
    isCoarse,
    actions,
    title
}) => {
    const {
        controlHeights,
        shapeConfigs,
        durations,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const isReduceMotionEnabled = useReducedMotion();

    const {
        container: containerDynamicStyle,
        layer: layerDynamicStyle,
        bar: barDynamicStyle
    } = useStyles({
        isReduceMotionEnabled,
        controlHeights,
        borderRadius,
        shapeConfigs,
        durations,
        isVisible,
        isCoarse,
        colors,
        spaces
    });

    const renderButton = (action: ISwipeableRowHoverProps["actions"][number]) => {
        return <Tooltip
            title={getSwipeableRowHoverTooltip({
                action
            })}
            customLocalize={customLocalize}
            customTheme={customTheme}
            key={action.key}
            side="top"
        >
            <Button
                type={action.type === "danger" ? "danger" : "neutral"}
                onPress={() => onActionPress(action)}
                accessibilityLabel={action.title}
                customTheme={customTheme}
                icon={action.icon}
                variant="ghost"
                size="small"
            />
        </Tooltip>;
    };

    return <View
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <View
            style={[
                stylesheet.bar,
                barDynamicStyle
            ]}
        >
            {
                !isCoarse && isVisible ?
                    <StateLayer
                        customTheme={customTheme}
                        style={layerDynamicStyle}
                        overlayColor="default"
                        isHovered={isHovered}
                        borderRadius={0}
                    />
                :
                    null
            }
            {isCoarse ? null : actions.slice(0, SWIPEABLE_ROW_MAX_BUTTONS).map(renderButton)}
            <DropdownMenu
                onOpenChange={onMenuOpenChange}
                customLocalize={customLocalize}
                customTheme={customTheme}
                title={title}
                align="end"
            >
                <DropdownMenu.Trigger>
                    <Button
                        accessibilityLabel={localize("more-actions")}
                        icon={SwipeableRowMoreIcon}
                        customTheme={customTheme}
                        onPress={handleNoop}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                </DropdownMenu.Trigger>
                <DropdownMenu.Content>
                    {menuItems}
                </DropdownMenu.Content>
            </DropdownMenu>
        </View>
    </View>;
};
export default SwipeableRowHover;
